import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchSubcategories,
    createSubCategory,
    updateSubCategory,
    deleteSubCategory,
    setPagination,
    setSort,
    searchSubcategories,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    resetFiltersAndPagination
} from '../../store/modules/subcategory/subcategoryActions';
import SubCategoryForm from '../../components/pageModule/Subcategories/SubCategoryForm';
import { useHasPermission } from '../../utils/permissionUtils';

const MySwal = withReactContent(Swal);

const Subcategories = ({ excludedActions = [], onSelectedRowsChange = () => { } }) => {

    const hasCreatePermission = useHasPermission(['create sub categories']);
    const hasUpdatePermission = useHasPermission(['update sub categories']);
    const hasViewPermission = useHasPermission(['view sub categories']);

    const subcategoryState = useSelector(state => state.subcategory);
    const dispatch = useDispatch();

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createSubCategory(formData));
            toast.success('SubCategory created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating subcategory', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateSubCategory(id, formData));
            toast.success('SubCategory updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating subcategory', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteSubCategory(id));
                toast.success('SubCategory deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting subcategory', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const columns = [
        {
            name: 'Name',
            selector: row => row.name?.en ?? '-',
            sortable: true,
            sortField: 'name',
        },
        {
            name: 'Category',
            selector: row => row.category?.label ?? '-',
        },
        {
            name: 'Status',
            maxWidth: '100px',
            selector: row => row.is_active ?
                <span className="badge bg-success">Active</span> :
                <span className="badge bg-danger">Inactive</span>,
        },
    ];


    const actions = {
        fetch: (queryFilter, activeFilter) => dispatch(fetchSubcategories(queryFilter, activeFilter)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchSubcategories(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter))
    };

    let defaultAvailableActions = ['search'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    return (
        <>
            <GenericPage
                title="Sub Categories"
                moduleName="SubCategory"
                moduleKey="subcategories"
                state={subcategoryState}
                actions={actions}
                FormComponent={SubCategoryForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                selectableRows={false}
                onSelectedRowsChange={onSelectedRowsChange}
            />
        </>
    );
};

export default Subcategories; 